import * as React from 'react'
import { parse as papaParse, unparse } from 'papaparse'
import ExcelJS from 'exceljs'
import { readCsvFile, readExcelFile } from '../../../../common/utils/FileUtils'
import { getMessage } from '../../../../common/utils/MessageUtils'
import { convertDateToJapaneseFormat } from '../../../../common/utils/DateUtils'
import { fetchPeriodList, fetchHierarchyCountByPeriod } from './services'
import { validateDepartment, validateHierarchyRow } from './validation'
import { MAX_HIERARCHY_LEVELS } from './store'

/** 削除・空欄表示用プレースホルダー（全角マイナス）。A3-002 */
export const DELETED_PLACEHOLDER = '−'

/** 半角・全角スペースを前後から削除 */
const TRIM_REGEX = /^[\s　]+|[\s　]+$/g

/**
 * 前後の半角・全角スペースをトリム
 *
 * @param {string | null | undefined} value - 対象文字列
 * @returns {string} トリム後の文字列
 */
export function trimBothSpaces(value) {
  if (value == null || typeof value !== 'string') return ''
  return value.replace(TRIM_REGEX, '')
}

/**
 * 組織略称が「欠損のダッシュ表記」か（半角・全角・Unicode ダッシュ／マイナス類）。 編集モーダルでは空欄＋プレースホルダー表示に合わせる。 対象例: `-` U+002D, `−`
 * U+2212（{@link DELETED_PLACEHOLDER}）, `－` U+FF0D, U+2010–U+2015, `﹣` U+FE63
 *
 * @param {string | null | undefined} value
 * @returns {boolean}
 */
export function isPlaceholderDashShortName(value) {
  const s = trimBothSpaces((value ?? '').toString())
  if (s === '') return true
  return /^[\u002D\u2010-\u2015\u2212\uFE63\uFF0D]+$/.test(s)
}

/**
 * 登録API用: 略称が未入力・プレースホルダー（− 等）のみなら null（DynamoDB NULL と整合）。
 *
 * @param {string | null | undefined} value
 * @returns {string | null}
 */
export function normalizeDepartmentShortNameForRegistration(value) {
  if (isPlaceholderDashShortName(value)) return null
  return trimBothSpaces(String(value ?? ''))
}

/**
 * 一覧・ツールチップ用: 欠損（null・空・ダッシュ類のみ）を {@link DELETED_PLACEHOLDER} に統一する内部処理。
 *
 * @param {string | null | undefined} value
 * @returns {string}
 */
function formatPlaceholderDashCellForDisplay(value) {
  if (isPlaceholderDashShortName(value)) return DELETED_PLACEHOLDER
  return trimBothSpaces((value ?? '').toString())
}

/**
 * 一覧・ツールチップ用: 組織略称が欠損（null・空・ダッシュ類のみ）なら {@link DELETED_PLACEHOLDER} に統一する。 API や CSV が `-` / `−` / `－`
 * 等を混在させても、欠損の見え方を1種類に揃える。
 *
 * @param {string | null | undefined} value
 * @returns {string}
 */
export function formatDepartmentShortNameForDisplay(value) {
  return formatPlaceholderDashCellForDisplay(value)
}

/**
 * 一覧・ツールチップ用: セグメント名が欠損（null・空・ダッシュ類のみ）なら {@link DELETED_PLACEHOLDER} に統一する。 組織略称列と同じ全角マイナス表記に揃える。
 *
 * @param {string | null | undefined} value
 * @returns {string}
 */
export function formatSegmentNameForDisplay(value) {
  return formatPlaceholderDashCellForDisplay(value)
}

/**
 * 日付文字列に日数を加算して YYYY/MM/DD で返す
 *
 * @param {string} dateStr - 開始日（YYYY/MM/DD または YYYY-MM-DD）
 * @param {number} days - 加算する日数（負の場合は減算）
 * @returns {string} YYYY/MM/DD
 */
export function addDaysToDateString(dateStr, days) {
  if (!dateStr || typeof days !== 'number') return ''
  const normalized = String(dateStr).trim().replace(/-/g, '/')
  const d = new Date(normalized)
  if (Number.isNaN(d.getTime())) return ''
  d.setDate(d.getDate() + days)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}/${m}/${day}`
}

/**
 * 期間表示名を生成（開始日 ～ 終了日）。終了日が空の場合は「開始日 ～ 」
 *
 * @param {string} startDate - 開始日（YYYY/MM/DD または YYYYMMDD）
 * @param {string} endDate - 終了日（空の場合は「 ～ 」のみ）
 * @returns {string}
 */
export function formatPeriodLabel(startDate, endDate) {
  if (!startDate) return ''
  const startStr = normalizeDateForDisplay(startDate)
  const startJp = toJapaneseDate(startStr)
  if (!endDate || String(endDate).trim() === '') {
    return `${startJp} ～ `
  }
  const endStr = normalizeDateForDisplay(endDate)
  const endJp = toJapaneseDate(endStr)
  return `${startJp} ～ ${endJp}`
}

/**
 * YYYYMMDD を YYYY/MM/DD に変換（convertDateToJapaneseFormat は / 区切りを想定）
 *
 * @param {string} dateStr - YYYYMMDD または YYYY/MM/DD
 * @returns {string} YYYY/MM/DD
 */
function normalizeDateForDisplay(dateStr) {
  if (!dateStr) return ''
  const s = String(dateStr).trim()
  if (s.length === 8 && /^\d{8}$/.test(s)) {
    return `${s.slice(0, 4)}/${s.slice(4, 6)}/${s.slice(6, 8)}`
  }
  return s
}

/**
 * 日付文字列を日本語形式（YYYY年MM月DD日）に変換
 *
 * @param {string} dateStr - YYYY/MM/DD
 * @returns {string}
 */
function toJapaneseDate(dateStr) {
  const normalized = normalizeDateForDisplay(dateStr)
  if (!normalized) return ''
  return convertDateToJapaneseFormat(normalized)
}

/**
 * 組織階層セルの表示（設計書 4.5: 矢印・色分け） hasUpperHierarchy / hasLowerHierarchy に応じて green--text / blue3--text 等を返す
 *
 * @param {string} cellValue - セル値
 * @param {number} columnIndex - 列インデックス（0〜5）
 * @param {string[]} rowData - 行データ（第1〜第6階層＋組織コード）
 * @param {number} hierarchyCount - 階層数（最大6）
 * @returns {{ text: string; className: string }}
 */
export function getHierarchyCellDisplay(cellValue, columnIndex, rowData, hierarchyCount) {
  const hasUpper = rowData
    .slice(0, columnIndex)
    .some((val) => val != null && String(val).trim() !== '' && String(val).trim() !== '-')
  const hasLower = rowData
    .slice(columnIndex + 1, hierarchyCount)
    .some((val) => val != null && String(val).trim() !== '' && String(val).trim() !== '-')
  const value = cellValue == null || String(cellValue).trim() === '' ? '-' : String(cellValue).trim()

  if (!hasUpper && hasLower) {
    return { text: value === '-' ? value : `${value} <`, className: 'green--text' }
  }
  if (hasUpper && !hasLower) {
    return { text: value, className: '' }
  }
  if (hasUpper && hasLower) {
    return { text: value === '-' ? value : `> ${value}`, className: 'blue3--text' }
  }
  return { text: value, className: '' }
}

/**
 * 組織階層テーブル表示用：空セルを上の行の値で埋めた行配列を返す（登録済みデータ表示時の「親組織を繰り返し表示」）
 *
 * @param {string[][]} rows - 階層データ（各行は 第1〜第6階層＋組織コード の7要素）
 * @param {number} hierarchyCount - 階層列数（6）
 * @returns {string[][]} 表示用行（各行は hierarchyCount 要素。空の場合は直前の行の同列の値で埋める）
 */
export function getHierarchyDisplayRows(rows, hierarchyCount = 6) {
  if (!Array.isArray(rows) || rows.length === 0) return []
  const count = Math.min(6, Math.max(0, hierarchyCount))
  const lastValues = Array(count).fill('')
  return rows.map((row) => {
    const displayRow = []
    for (let col = 0; col < count; col++) {
      const raw = (row[col] ?? '').toString().trim()
      const hasValue = raw !== '' && raw !== '-'
      if (hasValue) {
        lastValues[col] = raw
        displayRow[col] = raw
      } else {
        displayRow[col] = lastValues[col]
      }
    }
    return displayRow
  })
}

/**
 * 日付を YYYY/MM/DD に正規化（API 送信用）
 *
 * @param {string} dateStr - YYYY-MM-DD または YYYY/MM/DD または YYYYMMDD
 * @returns {string}
 */
export function toApiDate(dateStr) {
  if (!dateStr) return ''
  const s = String(dateStr).trim()
  if (s.length === 8 && /^\d{8}$/.test(s)) {
    return `${s.slice(0, 4)}/${s.slice(4, 6)}/${s.slice(6, 8)}`
  }
  if (s.indexOf('-') !== -1) {
    return s.replace(/-/g, '/')
  }
  return s
}

/**
 * 組織一覧を指定キーでソート
 *
 * @param {Object[]} list - 組織オブジェクトの配列（code, name, shortName, segment 等）
 * @param {string} sortKey - キー（'department_code' | 'department_name' | 'department_short_name' | 'segment_name'）
 * @param {'asc' | 'desc'} dir - ソート方向
 * @returns {Object[]} 新しい配列（非破壊）
 */
export function sortDepartmentList(list, sortKey, dir) {
  const arr = [...list]
  const mult = dir === 'asc' ? 1 : -1
  arr.sort((a, b) => {
    // 新規追加（status='new'）の項目は常に最後に配置
    const aIsNew = (a.status || '') === 'new'
    const bIsNew = (b.status || '') === 'new'
    if (aIsNew && !bIsNew) return 1 // a が新規 → 後ろ
    if (!aIsNew && bIsNew) return -1 // b が新規 → 後ろ
    if (aIsNew && bIsNew) return 0 // 両方新規 → 順序維持（追加順）
    // 通常のソート
    const va = (a[sortKey] ?? '').toString()
    const vb = (b[sortKey] ?? '').toString()
    if (va < vb) return -1 * mult
    if (va > vb) return 1 * mult
    return 0
  })
  return arr
}

/**
 * 階層列の一意な組織数をカウント（ヘッダー「第N階層 [数]」用）
 *
 * @param {number} columnIndex - 列インデックス
 * @param {string[][]} hierarchyData - 行配列
 * @returns {number} 一意な値の数
 */
export function getUniqueHierarchyCount(columnIndex, hierarchyData) {
  const values = hierarchyData
    .map((row) => row[columnIndex])
    .filter((val) => val != null && String(val).trim() !== '' && String(val).trim() !== '-')
  return new Set(values).size
}

/**
 * セグメント名からセグメントコードを取得（登録API用）。未選択・空白のみは null（DynamoDB NULL と整合）。
 *
 * @param {({ segment_name?: string; segment_code?: string } | string)[]} segmentList - セグメント一覧
 * @param {string} [segmentName] - セグメント名
 * @returns {string | null} セグメントコード。未入力・マスタ不一致でバリデーション外の場合は解決値（従来どおり）
 */
export function getSegmentCodeFromName(segmentList, segmentName) {
  if (!Array.isArray(segmentList)) return null
  const name = String(segmentName ?? '').trim()
  if (!name) return null
  for (const s of segmentList) {
    if (typeof s === 'string') {
      if (s === name) return s
    } else if (s && (s.segment_name === name || s.segment_code === name)) {
      return s.segment_code || s.segment_name || name
    }
  }
  return name
}

/**
 * 階層データを7列形式に正規化する
 *
 * APIレスポンスは2つのフォーマットがある：
 *
 * 1. 7列形式: [第1階層, 第2階層, ..., 第6階層, 組織コード]
 * 2. 可変列形式: [組織コード, 第1階層, 第2階層, ...]
 *
 * 内部的には常に7列形式（[0-5]=階層, [6]=組織コード）で扱う
 *
 * @param {(string | number)[]} row - 階層データ行
 * @returns {string[]} 7列形式の配列 [第1階層, ..., 第6階層, 組織コード]
 */
export function normalizeHierarchyRow(row) {
  const arr = Array(7).fill('')

  if (!Array.isArray(row) || row.length === 0) {
    return arr
  }

  if (row.length >= 7) {
    // 7列形式: そのままコピー
    for (let i = 0; i < 7; i++) {
      arr[i] = row[i] ?? ''
    }
  } else if (row.length >= 1) {
    // 可変列形式: 組織コードを末尾に移動
    arr[6] = row[0] // 組織コード
    for (let i = 1; i < row.length && i <= 6; i++) {
      arr[i - 1] = row[i] ?? '' // 階層データ
    }
  }

  return arr
}

/** 組織階層テーブル用：グラフ用ヘッダー（表示列） */
export const DEFAULT_HIERARCHY_HEADERS_GRAPH = [
  '所属組織1（グラフ用）',
  '所属組織2（グラフ用）',
  '所属組織3（グラフ用）',
  '所属組織4（グラフ用）',
  '所属組織5（グラフ用）',
  '所属組織6（グラフ用）'
]

/** 全角数字 → 半角数字（ヘッダー比較用）。API/S3のCSVが全角で返る場合に indexList を正しく得るため */
function normalizeHeaderCell(s) {
  if (s == null) return ''
  const str = String(s).trim()
  return str.replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
}

/**
 * ヘッダー行から「所属組織N（グラフ用）」列のインデックス配列を取得（HierarchyTable の indexList） 全角・半角の違いを吸収し、見つからなければ「第N階層」列のインデックスをフォールバックで返す。
 *
 * @param {string[]} headerRow - ヘッダー行（master または processed の先頭行）
 * @param {number} hierarchyCount - 階層数（1〜6）
 * @returns {number[]} 各表示列に対応する列インデックス
 */
export function buildHierarchyIndexList(headerRow, hierarchyCount) {
  if (!Array.isArray(headerRow) || hierarchyCount < 1) return []
  const headers = DEFAULT_HIERARCHY_HEADERS_GRAPH.slice(0, hierarchyCount)
  const normalizedRow = headerRow.map((cell) => normalizeHeaderCell(cell))
  const indexList = headers.map((head) => {
    const n = normalizeHeaderCell(head)
    const idx = normalizedRow.findIndex((cell) => cell === n)
    return idx >= 0 ? idx : -1
  })
  if (indexList.every((i) => i >= 0)) return indexList
  const fallback = []
  for (let i = 1; i <= hierarchyCount; i++) {
    const pattern = normalizeHeaderCell(`第${i}階層`)
    const idx = normalizedRow.findIndex((cell) => cell === pattern)
    fallback.push(idx >= 0 ? idx : -1)
  }
  return fallback.every((i) => i >= 0) ? fallback : indexList
}

/**
 * ヘッダーセルを正規化（BOM・前後空白除去）。API/S3のCSVで BOM や全角スペースが付く場合に一致させる
 *
 * @param {string} cell - ヘッダーセル
 * @returns {string}
 */
function normalizeOrgCodeHeaderCell(cell) {
  if (cell == null) return ''
  return String(cell)
    .replace(/\uFEFF/g, '')
    .replace(/\s/g, ' ')
    .trim()
}

/**
 * ヘッダー行から「組織コード」列のインデックスを取得（列順変更に対応：ヘッダー名で特定） 「組織コード」「department_code」（大文字小文字無視）のいずれかと一致する列を返す
 *
 * @param {string[]} headerRow - ヘッダー行
 * @returns {number} 組織コード列のインデックス（見つからなければ -1）
 */
export function getOrgCodeColumnIndex(headerRow) {
  if (!Array.isArray(headerRow)) return -1
  const idx = headerRow.findIndex((cell) => {
    const n = normalizeOrgCodeHeaderCell(cell)
    return n === '組織コード' || (typeof n === 'string' && n.toLowerCase() === 'department_code')
  })
  return idx >= 0 ? idx : -1
}

/**
 * Drilldown processed（2D、先頭行ヘッダー）から 7 列形式 [第1〜第6階層, 組織コード] を抽出（登録・エクスポート用）
 *
 * @param {string[][]} processedList - 加工済み階層データ（先頭行＝ヘッダー）
 * @param {number} hierarchyCount - 階層数
 * @returns {string[][]} 7 列の行配列（ヘッダーなし）
 */
export function extractSevenColFromProcessed(processedList, hierarchyCount) {
  if (!Array.isArray(processedList) || processedList.length < 2) return []
  const headerRow = processedList[0]
  const indexList = buildHierarchyIndexList(headerRow, hierarchyCount)
  const orgCodeIdx = getOrgCodeColumnIndex(headerRow)
  if (indexList.some((i) => i < 0) || orgCodeIdx < 0) return []

  return processedList.slice(1).map((row) => {
    const out = []
    for (let c = 0; c < hierarchyCount; c++) {
      out.push((row[indexList[c]] ?? '').toString().trim())
    }
    out.push((row[orgCodeIdx] ?? '').toString().trim())
    return out
  })
}

/**
 * API が「所属組織N（テーブル用）」ヘッダーで返す master から 7 列を抽出（新規期間コピー時のみ使用） buildHierarchyIndexList はグラフ用・表示用のため変更せず、こちらでテーブル用を認識する
 *
 * @param {string[][]} masterList - マスター階層データ（先頭行＝ヘッダー）
 * @param {number} hierarchyCount - 階層数（1〜6）
 * @returns {string[][]} 7 列の行配列（ヘッダーなし）。ヘッダーがテーブル用でない場合は []
 */
export function extractSevenColFromMasterTableStyle(masterList, hierarchyCount) {
  if (!Array.isArray(masterList) || masterList.length < 2 || hierarchyCount < 1) return []
  const headerRow = masterList[0]
  const normalizedRow = headerRow.map((cell) => normalizeHeaderCell(cell))
  const tableHeaders = [
    '所属組織1（テーブル用）',
    '所属組織2（テーブル用）',
    '所属組織3（テーブル用）',
    '所属組織4（テーブル用）',
    '所属組織5（テーブル用）',
    '所属組織6（テーブル用）'
  ].slice(0, hierarchyCount)
  const indexList = tableHeaders.map((head) => {
    const n = normalizeHeaderCell(head)
    const idx = normalizedRow.findIndex((cell) => cell === n)
    return idx >= 0 ? idx : -1
  })
  const orgCodeIdx = getOrgCodeColumnIndex(headerRow)
  if (indexList.some((i) => i < 0) || orgCodeIdx < 0) return []

  return masterList.slice(1).map((row) => {
    const out = []
    for (let c = 0; c < hierarchyCount; c++) {
      const value = (row[indexList[c]] ?? '').toString().trim()
      const cleanValue = value.replace(/^<+|>+$/g, '').trim()
      out.push(cleanValue)
    }
    out.push((row[orgCodeIdx] ?? '').toString().trim())
    return out
  })
}

/**
 * Master CSV（2D、先頭行ヘッダー）から 7 列形式 [第1〜第6階層, 組織コード] を抽出（登録・エクスポート用） processedと異なり、masterはdrilldown処理前のオリジナルデータ
 * drilldown記号（< >）を除去して返す
 *
 * @param {string[][]} masterList - マスター階層データ（先頭行＝ヘッダー）
 * @param {number} hierarchyCount - 階層数
 * @returns {string[][]} 7 列の行配列（ヘッダーなし、drilldown記号除去済み）
 */
export function extractSevenColFromMaster(masterList, hierarchyCount) {
  if (!Array.isArray(masterList) || masterList.length < 2) return []
  const headerRow = masterList[0]
  const indexList = buildHierarchyIndexList(headerRow, hierarchyCount)
  const orgCodeIdx = getOrgCodeColumnIndex(headerRow)
  if (indexList.some((i) => i < 0) || orgCodeIdx < 0) return []

  return masterList.slice(1).map((row) => {
    const out = []
    for (let c = 0; c < hierarchyCount; c++) {
      // drilldown記号（< >）を除去
      const value = (row[indexList[c]] ?? '').toString().trim()
      const cleanValue = value.replace(/^<+|>+$/g, '').trim()
      out.push(cleanValue)
    }
    out.push((row[orgCodeIdx] ?? '').toString().trim())
    return out
  })
}

/**
 * 表示順列のインデックスを取得
 *
 * @param {string[]} headerRow - ヘッダー行
 * @returns {number} 表示順の列インデックス（無ければ -1）
 */
export function getDisplayOrderColumnIndex(headerRow) {
  if (!Array.isArray(headerRow)) return -1
  const idx = headerRow.findIndex((cell) => String(cell).trim() === '表示順')
  return idx >= 0 ? idx : -1
}

/**
 * Master / processed を表示順でソート（先頭行はヘッダーのためそのまま）
 *
 * @param {string[][]} masterList - Master 2D（先頭行＝ヘッダー）
 * @param {string[][]} processedList - Processed 2D（先頭行＝ヘッダー）
 * @returns {{ masterList: string[][]; processedList: string[][] }}
 */
export function sortHierarchyByDisplayOrder(masterList, processedList) {
  if (
    !Array.isArray(masterList) ||
    masterList.length < 2 ||
    !Array.isArray(processedList) ||
    processedList.length < 2
  ) {
    return { masterList, processedList }
  }
  const headerRow = masterList[0]
  const orderIdx = getDisplayOrderColumnIndex(headerRow)
  if (orderIdx < 0) return { masterList, processedList }

  const masterData = masterList.slice(1)
  const processedData = processedList.slice(1)
  const indices = masterData.map((_, i) => i)
  indices.sort((a, b) => {
    const va = Number(masterData[a][orderIdx]) || 0
    const vb = Number(masterData[b][orderIdx]) || 0
    return va - vb
  })

  const sortedMaster = [masterList[0], ...indices.map((i) => masterData[i])]
  const sortedProcessed = [processedList[0], ...indices.map((i) => processedData[i])]
  return { masterList: sortedMaster, processedList: sortedProcessed }
}

/**
 * Master 2D のみを表示順でソート（API は drilldown の processed を返さないため）
 *
 * @param {string[][]} masterList - Master 2D（先頭行＝ヘッダー）
 * @returns {string[][]}
 */
export function sortHierarchyMasterOnly(masterList) {
  if (!Array.isArray(masterList) || masterList.length < 2) {
    return masterList
  }
  const orderIdx = getDisplayOrderColumnIndex(masterList[0])
  if (orderIdx < 0) return masterList

  const masterData = masterList.slice(1)
  const indices = masterData.map((_, i) => i)
  indices.sort((a, b) => {
    const va = Number(masterData[a][orderIdx]) || 0
    const vb = Number(masterData[b][orderIdx]) || 0
    return va - vb
  })

  return [masterList[0], ...indices.map((i) => masterData[i])]
}

/**
 * 7 列形式の階層データから、表示用の synthetic master / processed 2D を生成（インポート直後など API 未取得時） drilldown と同じ列構成: 所属組織1(テーブル用),
 * 所属組織1(グラフ用), ..., 所属組織6(テーブル用), 所属組織6(グラフ用), 組織コード
 *
 * @param {string[][]} sevenColRows - 7 列の行配列 [第1〜第6階層, 組織コード]
 * @param {number} hierarchyCount - 階層数（1〜6）
 * @returns {{ masterList: string[][]; processedList: string[][] }}
 */
export function buildSyntheticMasterProcessedFromSevenCol(sevenColRows, hierarchyCount) {
  const count = Math.min(6, Math.max(0, hierarchyCount))
  const headerRow = []
  for (let i = 1; i <= count; i++) {
    headerRow.push(`所属組織${i}（テーブル用）`)
    headerRow.push(`所属組織${i}（グラフ用）`)
  }
  headerRow.push('組織コード')
  headerRow.push('有効期限_開始')
  headerRow.push('有効期限_終了')
  headerRow.push('表示順')

  const masterList = [headerRow]
  const processedList = [headerRow]

  sevenColRows.forEach((row, rowIndex) => {
    const masterRow = []
    const processedRow = []
    for (let c = 0; c < count; c++) {
      const val = (row[c] ?? '').toString().trim() || ''
      masterRow.push(val)
      masterRow.push(val)
      processedRow.push(val)
      processedRow.push(val)
    }
    const orgCode = (row[count] ?? '').toString().trim() || ''
    masterRow.push(orgCode, '', '', String(rowIndex + 1))
    processedRow.push(orgCode, '', '', String(rowIndex + 1))
    masterList.push(masterRow)
    processedList.push(processedRow)
  })

  return { masterList, processedList }
}

/**
 * プレースホルダ判定（"(N)組織名" 形式）
 *
 * @param {string} value - チェックする値
 * @returns {boolean}
 */
export function isPlaceholderName(value) {
  if (!value) return false
  const trimmed = String(value).trim()
  return trimmed !== '' && trimmed !== '-' && /^[>]?\s*\(\d+\)\s*/.test(trimmed)
}

/**
 * 階層列から右端の実名を取得（空・"-"・プレースホルダを除外）
 *
 * @param {string[]} levelValues - 階層列の値
 * @returns {string}
 */
export function getRightmostRealName(levelValues) {
  const nonPlaceholder = levelValues
    .map((v) => String(v ?? '').trim())
    .filter((v) => v !== '' && v !== '-' && !isPlaceholderName(v))
  return nonPlaceholder.length ? nonPlaceholder[nonPlaceholder.length - 1] : ''
}

/**
 * 組織階層で使用されている組織コードのセットを取得
 *
 * @param {string[][]} currentHierarchy - 7列形式の階層データ
 * @param {string[][]} displayProcessedList - 表示用processed 2D
 * @param {number} orgCodeColIdx - 組織コード列インデックス
 * @param {number[]} hierarchyTableIndexList - 表示列インデックス
 * @param {Object[]} currentDepartments - 組織一覧
 * @param {Set<string>} [excludeCodes] - 除外する組織コードのセット（階層エラーがある行の組織コード）
 * @returns {Set<string>}
 */
export function getUsedDepartmentCodes(
  currentHierarchy,
  displayProcessedList,
  orgCodeColIdx,
  hierarchyTableIndexList,
  currentDepartments,
  excludeCodes = null
) {
  const usedCodes = new Set()
  const activeDepts = (currentDepartments || []).filter((d) => (d.status || '') !== 'deleted')

  const trim = (v) => (v == null ? '' : String(v).trim())

  const addIfCodeAndNameMatch = (code, leafName) => {
    if (!code) return
    const trimmedCode = trim(code)
    const trimmedName = trim(leafName)

    const dept = activeDepts.find((d) => trim(d.department_code ?? d.code ?? '') === trimmedCode)
    if (!dept) return

    const deptName = trim(dept.department_name ?? dept.name ?? '')
    // 完全一致、または同名区別のため付与されたバッチの親名サフィックス「組織名(親名)」形式も一致とみなす
    const nameMatches =
      trimmedName !== '' &&
      deptName !== '' &&
      (deptName === trimmedName ||
        (trimmedName.startsWith(deptName) &&
          trimmedName.length > deptName.length &&
          (trimmedName[deptName.length] === '(' || trimmedName[deptName.length] === '（') &&
          /[)）]\s*$/.test(trimmedName)))
    if (nameMatches) usedCodes.add(trimmedCode)
  }

  // displayProcessedListから抽出
  if (orgCodeColIdx >= 0 && Array.isArray(displayProcessedList) && displayProcessedList.length >= 2) {
    const indexList = hierarchyTableIndexList.filter((idx) => idx >= 0)
    for (let i = 1; i < displayProcessedList.length; i++) {
      const row = displayProcessedList[i] || []
      const code = trim(row[orgCodeColIdx])
      const levelValues = indexList.map((idx) => trim(row[idx]))
      const leafName = getRightmostRealName(levelValues)
      addIfCodeAndNameMatch(code, leafName)
    }
  }

  // currentHierarchyから抽出
  currentHierarchy.forEach((row) => {
    const code = trim(row[6])
    const levelCols = (row.slice(0, 6) || []).map(trim)
    const leafName = getRightmostRealName(levelCols)
    addIfCodeAndNameMatch(code, leafName)
  })

  // 階層エラーがある行の組織コードは「使用中」から除外する
  if (excludeCodes && excludeCodes.size > 0) {
    excludeCodes.forEach((code) => usedCodes.delete(code))
  }

  return usedCodes
}

/**
 * 組織一覧を登録用のtable_dataに変換
 *
 * @param {Object[]} departments - 組織一覧
 * @param {string} startDate - 開始日（API形式 YYYY/MM/DD）
 * @param {string | null} endDate - 終了日
 * @param {Object[]} segmentList - セグメント一覧
 * @param {string[]} replacedSearchKeys - 削除対象の search_key 一覧
 * @returns {Object[]}
 */
export function buildTableDataForRegistration(departments, startDate, endDate, segmentList, replacedSearchKeys = []) {
  const table_data = departments.map((d) => ({
    search_key: `${startDate}#${d.department_code || ''}`,
    department_code: d.department_code || '',
    department_name: d.department_name || '',
    department_short_name: normalizeDepartmentShortNameForRegistration(d.department_short_name),
    segment_code: getSegmentCodeFromName(segmentList, d.segment_name),
    start_date: startDate,
    end_date: endDate ?? null,
    is_delete: d.status === 'deleted'
  }))

  // インポートで削除された組織を追加
  replacedSearchKeys.forEach((sk) => {
    const departmentCode = sk.includes('#') ? sk.split('#').slice(1).join('#') : ''
    table_data.push({
      search_key: sk,
      department_code: departmentCode,
      department_name: DELETED_PLACEHOLDER,
      department_short_name: normalizeDepartmentShortNameForRegistration(DELETED_PLACEHOLDER),
      segment_code: null,
      start_date: startDate,
      end_date: endDate ?? null,
      is_delete: true
    })
  })

  return table_data
}

/** 階層列の値を正規化（登録・エクスポート共通）。"-"／"(N)組織名" は fill-in 表示のため空で送る */
export function normalizeHierarchyLevelValue(value) {
  const v = String(value ?? '').trim()
  if (v === '' || v === '-' || v === '－') return ''
  if (/^\(\d+\)/.test(v)) return ''
  return v
}

/**
 * 組織階層を登録用のhierarchy_dataに変換（リクエストは7列＋表示順のまま。org_code_1～6はIF側で付与）。 行の最後の列は常に組織コードとして扱う（API取得時は4階層なら5列 [lev1..lev4,
 * 組織コード] になるため）。
 *
 * @param {string[][]} hierarchy - 7列形式または 階層数+1 列の階層データ [第1〜第N階層名, 組織コード]
 * @returns {Object[]} 登録用オブジェクト配列（department_1..6, department_code, display_order）
 */
export function buildHierarchyDataForRegistration(hierarchy) {
  return (hierarchy || []).map((row, i) => {
    const raw = Array.isArray(row) ? row : []
    const len = raw.length
    const departmentCode = len > 0 ? String(raw[len - 1] ?? '').trim() : ''
    const levelValues = []
    for (let j = 0; j < 6 && j < len - 1; j++) {
      levelValues.push(normalizeHierarchyLevelValue(raw[j]))
    }
    while (levelValues.length < 6) levelValues.push('')
    const displayOrder = String(i + 1).padStart(30, '0')
    return {
      department_1: levelValues[0] ?? '',
      department_2: levelValues[1] ?? '',
      department_3: levelValues[2] ?? '',
      department_4: levelValues[3] ?? '',
      department_5: levelValues[4] ?? '',
      department_6: levelValues[5] ?? '',
      department_code: departmentCode,
      display_order: displayOrder
    }
  })
}

/**
 * 組織名末尾の重複回避サフィックス「（親組織名）」または「(親組織名)」を除去する
 *
 * 以下の2パターンを除去対象とする：
 *
 * 1. 括弧内に「/」を含む場合（複数親: 「(親1/親2/...)」形式）
 * 2. 括弧内の内容が orgNameSet に含まれる場合（単一親: 「(親1)」形式）
 *
 * 「（V2）」「（V3）」などバージョン番号は除去しない（orgNameSet に含まれず、/もない）
 *
 * @param {string | null | undefined} name - 組織名
 * @param {Set<string>} [orgNameSet] - 既知の組織名セット（単一親判定用）
 * @returns {string} サフィックス除去後の組織名
 */
export function stripDuplicateNameSuffix(name, orgNameSet = null) {
  if (!name) return name ?? ''
  const s = String(name).trim()
  // パターン1: 括弧内に「/」または「／」(全角)を含む（複数親）→ 除去
  if (/[\(（][^)）]*[\/／][^)）]*[\)）]\s*$/.test(s)) {
    return s.replace(/[\(（][^)）]*[\/／][^)）]*[\)）]\s*$/, '').trim()
  }
  // パターン2: 括弧内が既知の組織名と一致（単一親）→ 除去
  if (orgNameSet && orgNameSet.size > 0) {
    const m = s.match(/[\(（]([^)）]*)[\)）]\s*$/)
    if (m && orgNameSet.has(m[1])) {
      return s.replace(/[\(（][^)）]*[\)）]\s*$/, '').trim()
    }
  }
  return s
}

/**
 * 組織階層のバリデーションを実行してエラー件数と行別エラーを返す
 *
 * @param {Object[]} departmentList - 組織一覧（削除予定除く）
 * @param {string[][]} hierarchy - 組織階層データ（7列形式）
 * @param {string[][]} hierarchyMasterList - Master 2D（API取得時）
 * @param {number} hierarchyCount - 階層列数
 * @param {Function} validateHierarchyRow - バリデーション関数
 * @returns {{ errorCount: number; rowErrors: Object<string, Object> }}
 */
export function validateHierarchyErrors(
  departmentList,
  hierarchy,
  hierarchyMasterList,
  hierarchyCount,
  validateHierarchyRow
) {
  const activeDepts = (departmentList || []).filter((d) => (d.status || '') !== 'deleted')
  let validationRows
  if (Array.isArray(hierarchyMasterList) && hierarchyMasterList.length > 0) {
    const sortedMaster = sortHierarchyMasterOnly(hierarchyMasterList)
    validationRows = extractSevenColFromMaster(sortedMaster, hierarchyCount)
    if (validationRows.length === 0) {
      validationRows = extractSevenColFromMasterTableStyle(sortedMaster, hierarchyCount)
    }
  }
  if (!validationRows || validationRows.length === 0) {
    validationRows = Array.isArray(hierarchy) ? hierarchy : []
  }
  if (validationRows.length === 0) {
    return { errorCount: 0, rowErrors: {} }
  }
  const rowErrors = {}
  let errorCount = 0
  validationRows.forEach((row, idx) => {
    const err = validateHierarchyRow(row, activeDepts)
    if (err && Object.keys(err).length > 0) {
      rowErrors[String(idx)] = err
      errorCount += Object.keys(err).length
    }
  })
  return { errorCount, rowErrors }
}

/**
 * 7列インデックス基準の行エラーをテーブル表示用列インデックスに変換（B2-002）
 *
 * @param {Object<string, Object<number | string, string>>} rowErrors - 行別エラー（7列インデックス 0-6）
 * @param {number[]} indexList - HierarchyTableIndexList（グラフ用列の dataColIdx）
 * @param {number} orgCodeColIdx - 組織コード列インデックス（未使用だが呼び出し元と合わせて受け取る）
 * @param {string[][]} currentHierarchy - 現在の階層データ（行の最後の非空階層判定用）
 * @returns {Object<string, Object<number | string, string>>} テーブル列インデックスに変換した rowErrors
 */
export function convertHierarchyRowErrorsToTableIndex(rowErrors, indexList, orgCodeColIdx, currentHierarchy) {
  const result = {}
  Object.keys(rowErrors).forEach((rowKey) => {
    const rowErr = rowErrors[rowKey]
    const converted = {}
    const orgCodeError = rowErr[6] || rowErr['6']
    Object.keys(rowErr).forEach((k) => {
      const sevenColIdx = Number(k)
      if (Number.isNaN(sevenColIdx)) return
      if (sevenColIdx === 6) {
        converted[6] = rowErr[k]
        converted['6'] = rowErr[k]
      } else if (sevenColIdx >= 0 && sevenColIdx < 6) {
        const msg = rowErr[k]
        if (indexList && sevenColIdx < indexList.length) {
          const dataColIdx = indexList[sevenColIdx]
          if (dataColIdx != null && dataColIdx >= 0) {
            converted[dataColIdx] = msg
            converted[String(dataColIdx)] = msg
          } else {
            converted[sevenColIdx] = msg
            converted[String(sevenColIdx)] = msg
          }
        }
      }
    })
    if (orgCodeError && (orgCodeError.includes('未登録') || orgCodeError.includes('組織'))) {
      const rowIdx = Number(rowKey)
      const hierarchy = (currentHierarchy || [])[rowIdx]
      if (hierarchy && Array.isArray(hierarchy)) {
        let lastNonEmptyIdx = -1
        for (let i = 0; i < Math.min(hierarchy.length - 1, 6); i++) {
          if (hierarchy[i] && String(hierarchy[i]).trim() !== '' && String(hierarchy[i]).trim() !== '-') {
            lastNonEmptyIdx = i
          }
        }
        if (lastNonEmptyIdx >= 0 && indexList && lastNonEmptyIdx < indexList.length) {
          const nameDataColIdx = indexList[lastNonEmptyIdx]
          if (nameDataColIdx != null && nameDataColIdx >= 0) {
            converted[nameDataColIdx] = orgCodeError
            converted[String(nameDataColIdx)] = orgCodeError
          }
        }
      }
    }
    if (Object.keys(converted).length > 0) result[rowKey] = converted
  })
  return result
}

// ============================================================================
// Constants
// ============================================================================

/** 削除・復帰ボタンのホバー用疑似要素のクラス */
export const BUTTON_STYLES = {
  deleteButtonHover: [
    'before:absolute',
    'before:block',
    "before:content-['']",
    'before:h-[30px]',
    'before:w-[30px]',
    'before:z-10',
    'before:rounded-full',
    'before:hidden',
    'before:bg-gray-600/10'
  ].join(' ')
}

/** ファイル形式エラーメッセージ */
export const FILE_FORMAT_ERROR_MESSAGE = '正しい形式のファイルをアップロードしてください'

/**
 * Errors を ChrofyShowErrors 用に配列形式に正規化
 *
 * @param {Object} errors - エラーオブジェクト
 * @returns {Object} 正規化されたエラーオブジェクト（各値が配列形式）
 */
export function normalizeErrorsForShow(errors) {
  if (!errors || typeof errors !== 'object') return {}
  return Object.fromEntries(
    Object.entries(errors).map(([k, v]) => [k, Array.isArray(v) ? v : v != null ? [String(v)] : []])
  )
}

// ============================================================================
// Import/Export Functions
// ============================================================================

const ORG_HEADER_PATTERNS = {
  code: ['組織コード', 'code'],
  name: ['組織名', 'name'],
  shortName: ['組織略称', '略称', 'shortName'],
  segment: ['セグメント', 'セグメント名', 'segment']
}

const HIERARCHY_HEADERS = ['第1階層', '第2階層', '第3階層', '第4階層', '第5階層', '第6階層', '組織コード']

/**
 * ヘッダー行からパターンに一致する列インデックスを取得（大文字小文字無視）
 *
 * @param {string[]} headers - ヘッダー配列
 * @param {string[]} patterns - パターン配列
 * @returns {number} 列インデックス（-1 なら未検出）
 */
function findHeaderIndex(headers, patterns) {
  const lower = (headers || []).map((h) =>
    String(h ?? '')
      .toLowerCase()
      .trim()
  )
  for (const p of patterns) {
    const idx = lower.indexOf(String(p).toLowerCase())
    if (idx !== -1) return idx
  }
  return -1
}

/**
 * 組織一覧CSV/Excelを解析して行配列を返す（1行目はヘッダー想定）
 *
 * @param {File} file
 * @returns {Promise<{ headers: string[]; rows: string[][] }>}
 */
export async function parseOrgFile(file) {
  const name = (file.name || '').toLowerCase()
  if (name.endsWith('.csv')) {
    const data = await readCsvFile(file)
    const rows = Array.isArray(data) ? data : []
    const headers = (rows[0] || []).map((c) => String(c ?? '').trim())
    return { headers, rows: rows.slice(1) }
  }
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    const workbook = await readExcelFile(file)
    const sheet = workbook.worksheets[0]
    if (!sheet) return { headers: [], rows: [] }
    const rows = []
    sheet.eachRow((row, rowNumber) => {
      const cells = []
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        let v = cell.value
        if (v != null && typeof v === 'object' && typeof v.toString === 'function') v = v.toString()
        cells[colNumber - 1] = v != null ? String(v).trim() : ''
      })
      rows.push(cells)
    })
    const headers = (rows[0] || []).map((c) => String(c ?? '').trim())
    return { headers, rows: rows.slice(1) }
  }
  throw new Error('対応形式はCSVまたはExcel(.xlsx)です')
}

/**
 * 組織一覧インポート用バリデーション
 *
 * @param {string[][]} rows - データ行（ヘッダー除く）
 * @param {string[]} headers - ヘッダー行
 * @param {Set<string> | (string | Object)[]} segmentNames - 選択中期間（current
 *   period）のセグメント名一覧。値が空、またはダッシュ類のみのセルは未選択として扱う。この期間に存在しない非空セグメントはエラーになる
 * @param {Set<string>} existingCodes - 既存組織コード（重複チェック）
 * @returns {{ validRows: Object[]; errorCount: number; rowErrors: Map<number, Object> }} バリデーション結果
 */
export function validateOrgImport(rows, headers, segmentNames, existingCodes = new Set()) {
  const codeIdx = findHeaderIndex(headers, ORG_HEADER_PATTERNS.code)
  const nameIdx = findHeaderIndex(headers, ORG_HEADER_PATTERNS.name)
  const shortIdx = findHeaderIndex(headers, ORG_HEADER_PATTERNS.shortName)
  const segIdx = findHeaderIndex(headers, ORG_HEADER_PATTERNS.segment)

  const segList = Array.isArray(segmentNames) ? segmentNames : []
  const segmentSet = buildSegmentMasterValueSet(segList)
  const rowErrors = new Map()
  const result = []
  const codesSoFar = new Set(existingCodes)

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i] || []
    const code = trimBothSpaces((row[codeIdx] ?? '').toString())
    const name = trimBothSpaces((row[nameIdx] ?? '').toString())
    const shortName = trimBothSpaces(shortIdx >= 0 ? (row[shortIdx] ?? '').toString() : '')
    let segment = trimBothSpaces(segIdx >= 0 ? (row[segIdx] ?? '').toString() : '')
    if (isPlaceholderDashShortName(segment)) segment = ''

    const org = {
      department_code: code,
      department_name: name,
      department_short_name: shortName || DELETED_PLACEHOLDER,
      segment_name: segment
    }
    const errs = validateDepartment(org, codesSoFar, null, { forImport: true })
    if (segment && !segmentSet.has(segment)) {
      errs.segment_name = errs.segment_name || getMessage('E020004', 'セグメント')
    }
    if (Object.keys(errs).length > 0) {
      rowErrors.set(i, errs)
    }
    if (code) codesSoFar.add(code)
    result.push(org)
  }

  let errorCount = 0
  rowErrors.forEach(() => (errorCount += 1))
  return { validRows: result, errorCount, rowErrors }
}

/**
 * 組織編集モーダル保存後に、インポート行（_importRowIndex 付き）の orgRowErrors を更新するための1行検証。 validateOrgImport と同じルール（セグメント未登録は E020004 等）。
 *
 * @param {{
 *   department_code?: string
 *   department_name?: string
 *   department_short_name?: string
 *   segment_name?: string
 * }} org
 *   - 保存後の組織
 *
 * @param {(string | { segment_name?: string; segment_code?: string })[]} segmentNames - セグメント一覧
 * @param {Set<string>} existingCodes - 現在の組織コード一覧（編集行は excludeCode で重複除外）
 * @param {string | null} excludeCode - 重複チェックから除外するコード（編集行の確定コード）
 * @returns {{ [key: string]: string }} エラーがなければ {}
 */
export function validateOrgImportRowAfterEdit(org, segmentNames, existingCodes, excludeCode) {
  const code = trimBothSpaces(org.department_code ?? '')
  const name = trimBothSpaces(org.department_name ?? '')
  const shortName = trimBothSpaces(org.department_short_name ?? '')
  let segment = trimBothSpaces(org.segment_name ?? '')
  if (isPlaceholderDashShortName(segment)) segment = ''

  const segList = Array.isArray(segmentNames) ? segmentNames : []
  const segmentSet = buildSegmentMasterValueSet(segList)

  const orgForValidation = {
    department_code: code,
    department_name: name,
    department_short_name: shortName || DELETED_PLACEHOLDER,
    segment_name: segment
  }
  const errs = validateDepartment(orgForValidation, existingCodes, excludeCode, { forImport: true })
  if (segment && !segmentSet.has(segment)) {
    errs.segment_name = errs.segment_name || getMessage('E020004', 'セグメント')
  }
  return errs
}

/**
 * 期間のセグメントマスタから「有効値」の Set を作る（前後 trim。segment_name と segment_code の両方を入れる）
 *
 * @param {(string | { segment_name?: string; segment_code?: string })[]} segmentList
 * @returns {Set<string>}
 */
export function buildSegmentMasterValueSet(segmentList) {
  const segList = Array.isArray(segmentList) ? segmentList : []
  const set = new Set()
  for (const s of segList) {
    if (typeof s === 'string') {
      const t = trimBothSpaces(s)
      if (t) set.add(t)
      continue
    }
    if (!s || typeof s !== 'object') continue
    const sn = trimBothSpaces((s.segment_name ?? '').toString())
    const sc = trimBothSpaces((s.segment_code ?? '').toString())
    if (sn) set.add(sn)
    if (sc) set.add(sc)
  }
  return set
}

/**
 * セグメント一覧から有効値の Set（モーダルでマスタ外値を検出する用）
 *
 * @param {(string | { segment_name?: string; segment_code?: string })[]} segmentList
 * @returns {Set<string>}
 */
export function getSegmentNameSetFromList(segmentList) {
  return buildSegmentMasterValueSet(segmentList)
}

/**
 * 組織階層CSV/Excelを解析（7列: 第1〜第6階層、組織コード）
 *
 * @param {File} file
 * @returns {Promise<string[][]>}
 */
export async function parseHierarchyFile(file) {
  const name = (file.name || '').toLowerCase()
  if (name.endsWith('.csv')) {
    const data = await readCsvFile(file)
    const rows = Array.isArray(data) ? data : []
    return rows.slice(1)
  }
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    const workbook = await readExcelFile(file)
    const sheet = workbook.worksheets[0]
    if (!sheet) return []
    const rows = []
    sheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return
      const cells = []
      for (let c = 1; c <= 7; c++) {
        const cell = row.getCell(c)
        let v = cell?.value
        if (v != null && typeof v === 'object' && typeof v.toString === 'function') v = v.toString()
        cells.push(v != null ? String(v).trim() : '')
      }
      rows.push(cells)
    })
    return rows
  }
  throw new Error('対応形式はCSVまたはExcel(.xlsx)です')
}

/**
 * 組織階層インポート用バリデーション
 *
 * @param {string[][]} rows - 7列の行配列
 * @param {Object[]} departmentList - 組織一覧（department_code, department_name）
 * @param {{ departmentNameFormat?: string }} [options] - Batch と同一の DepartmentNameFormat（未指定時は "({num}){name}"）
 * @returns {{ validRows: string[][]; errorCount: number; rowErrors: Map<number, Object> }} バリデーション結果
 */
export function validateHierarchyImport(rows, departmentList, options = {}) {
  const rowErrors = new Map()
  const activeDepts = (departmentList || []).filter((d) => (d.status || '') !== 'deleted')
  const seen = new Set()
  const formatOpt = { departmentNameFormat: options.departmentNameFormat }

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i] || []
    while (row.length < 7) row.push('')
    const errs = validateHierarchyRow(row, activeDepts, formatOpt) || {}
    const key = `${(row[6] || '').trim()}#${(
      row
        .slice(0, 6)
        .filter((v) => v && v !== '-')
        .pop() || ''
    ).trim()}`
    if (key && key !== '#') {
      if (seen.has(key)) {
        errs[6] = errs[6] || getMessage('E020003')
      } else {
        seen.add(key)
      }
    }

    // E020004 ("未登録の「組織」です。") の場合、組織名列にもエラーを表示
    const orgCodeError = errs[6]
    if (orgCodeError && (orgCodeError.includes('未登録') || orgCodeError.includes('組織'))) {
      // 最後の非空階層列のインデックスを見つける（組織コード列を除く）
      let lastNonEmptyIdx = -1
      for (let j = 0; j < 6; j++) {
        if (row[j] && String(row[j]).trim() !== '' && String(row[j]).trim() !== '-') {
          lastNonEmptyIdx = j
        }
      }
      // 組織名列にもエラーを設定
      if (lastNonEmptyIdx >= 0) {
        errs[lastNonEmptyIdx] = orgCodeError
      }
    }

    if (Object.keys(errs).length > 0) rowErrors.set(i, errs)
  }

  const errorCount = rowErrors.size
  return { validRows: rows, errorCount, rowErrors }
}

/**
 * 組織一覧をCSVでダウンロード
 *
 * @param {Object[]} list - 削除予定除く組織一覧
 * @param {string} periodKey - 対象期間キー（YYYY/MM/DD 等。ファイル名は YYYY-MM-DD 形式）
 * @returns {void}
 */
export function exportOrgCsv(list, periodKey) {
  const headers = ['組織コード', '組織名', '組織略称', 'セグメント名']
  const rows = (list || [])
    .filter((d) => (d.status || '') !== 'deleted')
    .map((d) => [
      d.department_code ?? '',
      d.department_name ?? '',
      d.department_short_name ?? DELETED_PLACEHOLDER,
      d.segment_name ?? DELETED_PLACEHOLDER
    ])
  const csv = unparse({ fields: headers, data: rows }, { quotes: true })
  const BOM = '\uFEFF'
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8' })
  const name = `組織一覧_${(periodKey || '').replace(/\//g, '-')}.csv`
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = name
  a.click()
  URL.revokeObjectURL(a.href)
}

/**
 * 組織一覧をExcelでダウンロード
 *
 * @param {Object[]} list - 削除予定除く組織一覧
 * @param {string} periodKey - 対象期間キー（YYYY/MM/DD 等。ファイル名は YYYY-MM-DD 形式）
 * @returns {Promise<void>}
 */
export async function exportOrgExcel(list, periodKey) {
  const workbook = new ExcelJS.Workbook()
  const sheet = workbook.addWorksheet('組織一覧')
  const headers = ['組織コード', '組織名', '組織略称', 'セグメント名']
  sheet.addRow(headers)
  sheet.getRow(1).font = { bold: true }
  ;(list || [])
    .filter((d) => (d.status || '') !== 'deleted')
    .forEach((d) => {
      sheet.addRow([
        d.department_code ?? '',
        d.department_name ?? '',
        d.department_short_name ?? DELETED_PLACEHOLDER,
        d.segment_name ?? DELETED_PLACEHOLDER
      ])
    })
  const buf = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const name = `組織一覧_${(periodKey || '').replace(/\//g, '-')}.xlsx`
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = name
  a.click()
  URL.revokeObjectURL(a.href)
}

/**
 * 組織階層をCSVでダウンロード
 *
 * @param {string[][]} rows - 階層データ行配列
 * @param {string} periodKey - 対象期間キー（YYYY/MM/DD 等。ファイル名は YYYY-MM-DD 形式）
 * @returns {void}
 */
export function exportHierarchyCsv(rows, periodKey) {
  const data = [HIERARCHY_HEADERS, ...(rows || [])]
  const csv = unparse({ data }, { quotes: true })
  const BOM = '\uFEFF'
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8' })
  const name = `組織階層_${(periodKey || '').replace(/\//g, '-')}.csv`
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = name
  a.click()
  URL.revokeObjectURL(a.href)
}

/**
 * 組織階層をExcelでダウンロード
 *
 * @param {string[][]} rows - 階層データ行配列
 * @param {string} periodKey - 対象期間キー（YYYY/MM/DD 等。ファイル名は YYYY-MM-DD 形式）
 * @returns {Promise<void>}
 */
export async function exportHierarchyExcel(rows, periodKey) {
  const workbook = new ExcelJS.Workbook()
  const sheet = workbook.addWorksheet('組織階層')
  sheet.addRow(HIERARCHY_HEADERS)
  sheet.getRow(1).font = { bold: true }
  ;(rows || []).forEach((r) => sheet.addRow(r))
  const buf = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const name = `組織階層_${(periodKey || '').replace(/\//g, '-')}.xlsx`
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = name
  a.click()
  URL.revokeObjectURL(a.href)
}

// ============================================================================
// DepInfoHierarchySetting モーダル（幅定数・説明文 JSX）
// ============================================================================

/** 登録確認・期間変更確認などで共通利用するモーダル幅 */
export const DEPARTMENT_MODAL_STYLE_NARROW_PADDED = Object.freeze({
  width: '640px',
  paddingLeft: '32px',
  paddingRight: '32px'
})

/** 新規期間作成モーダル幅 */
export const DEPARTMENT_MODAL_STYLE_NEW_PERIOD = Object.freeze({
  width: '600px'
})

/**
 * 期間切替時の未保存警告（説明文）
 *
 * @returns {JSX.Element}
 */
export function DepartmentPeriodChangeUnsavedWarningDescription() {
  return (
    <>
      データの登録が完了していません。 この状態で他の期間を選択すると、
      <span className="red--text whitespace-nowrap">変更内容が削除されます、</span>
      よろしいですか？
    </>
  )
}

/**
 * 全件削除ルートの「マスター設定値登録の確認」モーダル本文
 *
 * @returns {JSX.Element}
 */
export function DepartmentRegisterFlowDeletePeriodConfirmDescription() {
  return (
    <>
      <p className="red--text">選択中の対象期間の組織情報をすべて削除すると、当該期間が一覧から削除されます。</p>
      <p>設定値を登録してダッシュボードに反映します。よろしいですか</p>
      <p>※ダッシュボードへの反映には5分～10分程度かかります</p>
      <p>マスター管理画面のステータスから反映状況を確認してください</p>
    </>
  )
}

/**
 * 期間削除（フッター）確認モーダル本文
 *
 * @param {boolean} isRegistered - 登録済み期間か
 * @returns {JSX.Element}
 */
export function getDepartmentPeriodDeletionConfirmDescription(isRegistered) {
  if (isRegistered) {
    return (
      <>
        <p className="red--text">選択中の対象期間に該当する組織情報設定をすべて削除します。</p>
        <p>本操作は取り消すことができません。実行してよろしいですか?</p>
        <p className="mt-2 text-caption-2">※ダッシュボードへの反映には5分~10分程度かかります</p>
        <p className="text-caption-2">マスター管理画面のステータスから反映状況を確認してください</p>
      </>
    )
  }
  return (
    <>
      <p className="red--text">選択中の対象期間に該当する組織情報設定をすべて削除します。</p>
      <p>本操作は取り消すことができません。実行してよろしいですか?</p>
    </>
  )
}

// ============================================================================
// Custom Hooks
// ============================================================================

/**
 * 組織情報設定画面の初期化処理を行うカスタムフック。 期間一覧と各期間の階層列数を並列取得し、開始日の降順で並べた先頭を「最新期間」として currentPeriodKey に載せる。 マウント1回のみ動かすため effect
 * の依存は空配列。法人切替は親で Provider を張り替える想定。
 *
 * @param {string} corporationNumber - 法人番号
 * @param {Function} dispatch - ストアのdispatch関数
 * @param {string} defaultTab - デフォルトタブ（TAB_ORG または TAB_HIERARCHY）
 * @returns {{ isInitialized: boolean; error: Error | null; firstPeriodKey: string }} FirstPeriodKey は最新期間のキー（初回表示用）
 */
export function useDepartmentInitialization(corporationNumber, dispatch, defaultTab = 'TAB_ORG') {
  const [isInitialized, setIsInitialized] = React.useState(false)
  const [error, setError] = React.useState(null)
  const [firstPeriodKey, setFirstPeriodKey] = React.useState('')
  const initCorpNumberRef = React.useRef(null)

  React.useEffect(() => {
    if (!corporationNumber || isInitialized || initCorpNumberRef.current === corporationNumber) return

    initCorpNumberRef.current = corporationNumber
    let cancelled = false

    const initialize = async () => {
      try {
        const [periodListRaw, hierarchyCountMap] = await Promise.all([
          fetchPeriodList(corporationNumber),
          fetchHierarchyCountByPeriod(corporationNumber)
        ])

        if (cancelled) return

        // 期間一覧の処理
        const list = (Array.isArray(periodListRaw) ? periodListRaw : [])
          .map((p) => {
            // 対象期間は DepartmentInfo から取得（StartDate/EndDateを使用）
            const startDate =
              toApiDate(
                p.StartDate ??
                  p.start_date ??
                  p.startDate ??
                  (typeof p.SearchKey === 'string' && p.SearchKey.includes('#') ? p.SearchKey.split('#')[0] : '')
              ) || ''

            const endDate = toApiDate(p.EndDate ?? p.end_date ?? p.endDate ?? '') || ''

            const hCount = hierarchyCountMap?.[startDate] ?? p.DepartmentHierarchyCount ?? MAX_HIERARCHY_LEVELS

            return {
              key: startDate,
              startDate,
              endDate,
              label: formatPeriodLabel(startDate, endDate),
              DepartmentHierarchyCount: hCount,
              registration_status: p.registration_status ?? 'registered'
            }
          })
          .filter((p) => p.key)
          .reduce((acc, p) => (acc.some((x) => x.key === p.key) ? acc : acc.concat(p)), [])
          // 最新期間を先頭に（key は YYYY/MM/DD のため文字列比較で日付順）
          .sort((a, b) => (b.key < a.key ? -1 : b.key > a.key ? 1 : 0))

        const latestPeriodKey = list[0]?.key ?? ''
        const hierarchyCount = list[0]?.DepartmentHierarchyCount ?? MAX_HIERARCHY_LEVELS

        // 期間リストと選択キーを先に反映し、続けて「初期化完了」「タブ」を確定する（reducer が action 種別ごとに分かれているため複数回 dispatch）
        dispatch('SET_PERIOD_LIST', {
          periodList: list,
          currentPeriodKey: latestPeriodKey,
          hierarchyCount
        })
        dispatch('SET_INIT_DONE', true)
        dispatch('SET_ACTIVE_TAB', defaultTab === 'hierarchy' ? 'hierarchy' : 'org')

        setIsInitialized(true)
        setFirstPeriodKey(latestPeriodKey)
      } catch (err) {
        if (!cancelled) {
          setError(err)
          dispatch('SET_TOP_ERROR', err?.message || '初期化に失敗しました')
        }
      }
    }

    initialize()

    return () => {
      cancelled = true
    }
  }, [])

  return { isInitialized, error, firstPeriodKey }
}
